import React, { useState } from 'react'
import {
  useWatch,
  UseFormSetValue,
  FieldValues,
  UseFormGetValues,
  Control
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Icon, Segment } from 'semantic-ui-react'
import FileUpload from '~/components/FileUpload'
import { FormFields } from '~/components/FormFields'
import notify from '~/components/Notify'
import { StyledDeleteButton, StyledFileLabel, StyledFileWrapper } from './style'
import { b64toBlob, handleErrors } from '~/utils/helpers'
import Dialog from '~/components/Dialog'
import StockEchangeService from '~/api/services/stockExchange'
import { useParams } from 'react-router-dom'

interface LanguageTabProps {
  index: number
  control: Control
  setValue: UseFormSetValue<FieldValues>
  getValues: UseFormGetValues<FieldValues>
  language: any
  languages: any[]
}

const LanguageTab: React.FunctionComponent<LanguageTabProps> = (props) => {
  const { t } = useTranslation()
  const { id } = useParams()
  const [isBusy, setIsBusy] = useState(false)
  const [fileSource, setFileSource] = useState(null)
  const { index, control, getValues, setValue, languages, language } = props
  const indexMapped = languages.findIndex(languageItem => languageItem.id === language.id)
  const [openDeleteFileModal, setOpenFileDeleteModal] = useState(false)
  const indexValue = index || indexMapped
  const watchFile = useWatch({ control: control, name: `languages[${index}].file` })
  const watchFileName = useWatch({ control: control, name: `languages[${index}].originalFilename` })
  const watchFilename = useWatch({ control: control, name: `languages[${index}].filename` })
  const watchDescription = useWatch({ control: control, name: `languages[${index}].description` })

  async function handleDownloadPdf() {
    setIsBusy(true);
    try {
      if (watchFile) {
        setFileSource(watchFile)
      } else {
        const fileName = getValues(`languages[${index}].filename`)
        const response = await StockEchangeService.getFileTradingSlot(id, { fileName: fileName });
        const type = response.data.split(';')[0].split('data:')[1]
        const blob = b64toBlob(response.data, type);
        const blobUrl = URL.createObjectURL(blob);
        setFileSource(blobUrl);
      }
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function renderFileField() {
    return <FormFields.External
      control={control}
      name={`languages[${index}].file`}
      label={t('File')}
      onChange={(value, name) => {
        setValue(`languages[${index}].file`, value);
        setValue(`languages[${index}].originalFilename`, name);
      }}
    >
      <FileUpload
        type='button'
        encodeFiles
        accept={{
          'application/pdf': ['.pdf'],
          'application/msword': ['.doc'],
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
          'text/csv': ['.csv'],
          'application/vnd.ms-excel': ['.xls'],
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
        }}
        sizeLimitInMB={10}
      />
    </FormFields.External>
  }

  function renderModalPreviewFile() {
    return <Dialog
      open={Boolean(fileSource)}
      handleClose={() => setFileSource(null)}
      textContent={<iframe width='100%' height='100%' src={fileSource}></iframe>}
      textHeader={t('Preview')}
      textClose={t('Close')}
      type={'preview'}
    />
  }

  function removeFile(index) {
    if (id) {
      setOpenFileDeleteModal(true)
    } else {
      setValue(`languages[${index}].file`, null)
      setValue(`languages[${index}].originalFilename`, null)
    }
  }

  function renderFile() {
    return <StyledFileWrapper>
      <StyledFileLabel
        basic
        color='blue'
        onClick={() => handleDownloadPdf()}
      >
        <Icon name='file' />
        <div className='fileName'>{watchFileName}</div>
      </StyledFileLabel>

      <StyledDeleteButton
        onClick={() => removeFile(index)}
        rightAlign={10}
        size={'small'}
        basic
        color='red'
        type={'button'}
        icon='trash' />
    </StyledFileWrapper>
  }

  async function handleSubmit() {
    setIsBusy(true);
    try {
      await StockEchangeService.deleteFileTradingSlot(id, { fileName: watchFilename })
      setValue(`languages[${index}].filename`, null)
      setValue(`languages[${index}].originalFilename`, null)
      setOpenFileDeleteModal(false)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function renderDeleteFileModal() {
    return <Dialog
      textHeader={t('Delete file')}
      textContent={t('Do you want to delete {{watchFileName}}?', { watchFileName })}
      open={openDeleteFileModal}
      handleClose={() => setOpenFileDeleteModal(false)}
      textClose={t('No')}
      textSubmit={t('Yes')}
      isLoading={isBusy}
      type={'abort'}
      handleSubmit={() => handleSubmit()}
    />
  }

  return <div>
    {renderDeleteFileModal()}
    <FormFields.TextArea
      control={control}
      placeholder={t('Enter info')}
      style={{ marginBottom: 0 }}
      label={t('Info')}
      rules={{
        maxLength: 1000
      }}
      noErrorMessage
      name={`languages[${indexValue}].description`}
    />
    <small>{t('Characters: {{count}} (max 1000)', { count: watchDescription?.length || 0 })}</small>
    {fileSource && renderModalPreviewFile()}
    <Segment loading={isBusy} style={{ position: 'relative', minWidth: 430 }}>
      {watchFileName && renderFile()}
      {renderFileField()}
    </Segment>
  </div>

}
export default LanguageTab
